import { useState, useEffect } from 'react';
import { themeI18n } from '../utils/i18n_helper';
import { Map, useMap, useMapsLibrary, InfoWindow } from '@vis.gl/react-google-maps';
import { getBreakpoint } from '../utils/breakpoints'
import { fetchStoreMetaobjects, getDistance } from '../utils/store-locator';
import { ButtonContent } from './ButtonContent';

import { StoreLocatorMarker } from './StoreLocatorMarker';
import { StoreLocation } from './StoreLocation';

const cachedStoreLocations = JSON.parse(localStorage.getItem('bentley_store_locations'));
const userLocation = JSON.parse(localStorage.getItem('bentley_user_location'));

export const StoreLocatorSection = () => {
  const [storeLocations, setStoreLocations] = useState(cachedStoreLocations);
  const [userAddress, setUserAddress] = useState('');
  const [distanceFilter, setDistanceFilter] = useState(9999999999999);
  const [userLatLng, setUserLatLng] = useState({ lat: userLocation?.lat, lng: userLocation?.lng });
  const [loading, setLoading] = useState(true);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [infoWindowStore, setInfoWindowStore] = useState({});
  const [apiLoaded, setApiLoaded] = useState(false); // checks if google maps api is loaded

  const map = useMap(getBreakpoint() == 'mobile' || getBreakpoint() == 'tablet' ? 'store-locator-map-mobile' : 'store-locator-map'); // gives access to the instanced map object

  const geocodingLibrary = useMapsLibrary('geocoding');
  const coreLibrary = useMapsLibrary('core');

  const fetchStoreLocations = async () => {
    const stores = await fetchStoreMetaobjects();

    // if store locations are already set, make sure to not overwrite old stores (to keep distance data)
    if (storeLocations) {
      const storeUpdates = stores.map((store) => {
        const oldStore = storeLocations.find((store2) => store2.id === store.id);

        if (oldStore) {
          return {...oldStore, ...store};
        } else {
          if (userLatLng.lat && userLatLng.lng) {
            const distance = getDistance(userLatLng.lat, parseFloat(store.latitude), userLatLng.lng,  parseFloat(store.longitude));
            return {...store, distance};
          } else {
            return store;
          }
        }
      });

      setStoreLocations(storeUpdates.sort((a, b) => a.distance - b.distance));
      localStorage.setItem('bentley_store_locations', JSON.stringify(storeUpdates));
    } else {
      setStoreLocations(stores);
      localStorage.setItem('bentley_store_locations', JSON.stringify(stores));
    }

    setLoading(false);
  };

  const geocodeAddress = () => {
    setLoading(true);

    const geocoder = new geocodingLibrary.Geocoder();

    geocoder.geocode({ address: userAddress }, (results, status) => {
      if (status === 'OK') {
        setUserLatLng({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });

        if (distanceFilter == "9999999999999") {
          setDistanceFilter("25");
        }

        if (window.toast) {
          window.toast.success({
            message: themeI18n('store_locator.address_success')
          });
        }
      } else {
        if (window.toast) {
          window.toast.error({
            message: themeI18n('store_locator.address_error')
          });
        }
      }
    });

    setLoading(false);
  }

  const sortStoresByDistance = () => {
    const updatedStores = storeLocations.map(store => {
      const distance = getDistance(userLatLng.lat, parseFloat(store.latitude), userLatLng.lng,  parseFloat(store.longitude));
      return {
        ...store,
        distance,
      };
    });

    setStoreLocations(updatedStores.sort((a, b) => a.distance - b.distance));
    localStorage.setItem('bentley_store_locations', JSON.stringify(updatedStores)); // update cached store locations
  }

  useEffect(() => {
    fetchStoreLocations();

    const checkApiLoaded = setInterval(() => {
      if (window.google && window.google.maps.Map) {
        setApiLoaded(true);
        clearInterval(checkApiLoaded);
      }
    }, 100);

    return () => clearInterval(checkApiLoaded);
  }, []);

  useEffect(() => {
    if (storeLocations && coreLibrary) {
      const bounds = new coreLibrary.LatLngBounds();
      storeLocations.filter((store)=> (store.distance ? store.distance : 0) < distanceFilter).forEach(store => {
        bounds.extend({lat: parseFloat(store.latitude), lng: parseFloat(store.longitude)});
      });

      // zoom and pan to fit all store location markers
      map?.setCenter(bounds.getCenter());
      map?.fitBounds(bounds);
    }

    setInfoWindowOpen(false);
  }, [storeLocations, distanceFilter, map]);

  useEffect(() => {
    if (storeLocations && userLatLng.lat && userLatLng.lng) {
      sortStoresByDistance();
      localStorage.setItem('bentley_user_location', JSON.stringify(userLatLng));
    }
  }, [userLatLng]);

  const RenderInfoWindow = () => {
    return (
      <InfoWindow
        position={{lat: parseFloat(infoWindowStore.latitude), lng: parseFloat(infoWindowStore.longitude)}}
        onCloseClick={() => {
          setInfoWindowOpen(false);
        }}
        pixelOffset={{width: 0, height: -70}}
      >
        <div className='store-info-window'>
          <div className='store-info-window__name-distance-wrapper'>
            <h4 className='store-info-window__name'>{infoWindowStore.name}</h4>

            {infoWindowStore.distance && (
              <span className='store-info-window__distance'>
                {infoWindowStore.distance.toFixed(2)} km
              </span>
            )}
          </div>
          <p className='store-info-window__address'>{infoWindowStore.address}</p>

          <a href={`/pages/store/${infoWindowStore.handle}`} className='store-info-window__details-link'>
            {themeI18n('store_locator.store_details')}
          </a>
        </div>
      </InfoWindow>
    )
  }

  return (
    <div className="store-locator__content">
      <div className='store-locator__controls'>
        <form className='store-locator__form' onSubmit={(event)=> {
          event.preventDefault();

          geocodeAddress(event);
        }}>
          <div className="field">
            <label htmlFor="postal-code">
              {themeI18n('store_locator.address_label')}
            </label>

            <input type="text" id="postal-code" value={userAddress} onChange={(event) => setUserAddress(event.target.value)} required placeholder={themeI18n('store_locator.address_placeholder_section')}/>
          </div>

          <div className="field">
            <label htmlFor="distance-filter">
              {themeI18n('store_locator.distance')}
            </label>

            <select id='distance-filter' value={distanceFilter} onChange={(event) => setDistanceFilter(event.target.value)}>
              <option value="9999999999999">{themeI18n('store_locator.all_stores')}</option>
              <option value="5">5km</option>
              <option value="10">10km</option>
              <option value="25">25km</option>
              <option value="50">50km</option>
            </select>

            <span className='store-locator__results-count'>
              {(distanceFilter != "9999999999999" && userLatLng.lat) ? (
                <>{themeI18n('store_locator.showing')} {storeLocations?.filter((store)=> (store.distance ? store.distance : 0) < distanceFilter).length} {themeI18n('store_locator.stores_within')} {distanceFilter}km</>
              )
              : (
                <>{themeI18n('store_locator.showing')} {storeLocations?.filter((store)=> (store.distance ? store.distance : 0) < distanceFilter).length} {themeI18n('store_locator.stores')}</>
              )}
            </span>
          </div>

          <button className='store-locator__search-button button' type="submit">
            <ButtonContent text={themeI18n('store_locator.address_submit')} />
          </button>
        </form>

        {apiLoaded && (
          <Map
            className='store-locator__map hide-desktop-up'
            defaultCenter={{lat: 22.54992, lng: 0}}
            defaultZoom={3}
            disableDefaultUI={true}
            styles={
              [
                {
                  stylers: [{saturation: -100}]
                },
                {
                  featureType: 'poi',
                  stylers: [{ visibility: "off" }]
                },
                {
                  featureType: "transit",
                  elementType: "labels.icon",
                  stylers: [{ visibility: "off" }]
                }
              ]
            }
            id='store-locator-map-mobile'
          >
            {storeLocations?.filter((store)=> (store.distance ? store.distance : 0) < distanceFilter).map(store => {
              return (
                <StoreLocatorMarker key={store.id} store={store} setInfoWindowOpen={setInfoWindowOpen} setInfoWindowStore={setInfoWindowStore}/>
              )
            })}

            {infoWindowOpen && <RenderInfoWindow/>}
          </Map>
        )}

        <div className={loading ? 'store-locator__stores-list loading': 'store-locator__stores-list'}>
          {storeLocations?.filter((store)=> (store.distance ? store.distance : 0) < distanceFilter).map(store => {
            return (
              <StoreLocation key={store.id} store={store} map={map} infoWindowOpen={infoWindowOpen} infoWindowStore={infoWindowStore}/>
            )
          })}
        </div>
      </div>

      {apiLoaded && (
        <Map
          className='store-locator__map hide-tablet-down'
          defaultCenter={{lat: 22.54992, lng: 0}}
          defaultZoom={3}
          gestureHandling={'greedy'}
          disableDefaultUI={true}
          styles={
            [
              {
                stylers: [{saturation: -100}]
              },
              {
                featureType: 'poi',
                stylers: [{ visibility: "off" }]
              },
              {
                featureType: "transit",
                elementType: "labels.icon",
                stylers: [{ visibility: "off" }]
              }
            ]
          }
          id='store-locator-map'
        >
          {storeLocations?.filter((store)=> (store.distance ? store.distance : 0) < distanceFilter).map(store => {
            return (
              <StoreLocatorMarker key={store.id} store={store} setInfoWindowOpen={setInfoWindowOpen} setInfoWindowStore={setInfoWindowStore}/>
            )
          })}

          {infoWindowOpen && <RenderInfoWindow/>}
        </Map>
      )}
    </div>
  )
}
